import { normalizeMatchSnapshot as normalizeCanonicalMatch, mergeMatchSnapshots, isCompletedSet } from '../data/matchState.js';
import { canonicalTeamSlug } from '../data/teamIdentity.js';
import { repairUtf8Text } from '../data/textEncoding.js';
import { readPlayerJersey, normalizeJerseyNumber } from '../data/playerIdentity.js';
import { fetchApiJson } from './apiRequest.js';

const DEFAULT_ERROR = 'Match flow is temporarily unavailable.';
const MAX_DEPTH = 10;
const MAX_EVENTS = 600;
const MAX_PLAYERS = 60;
const RESPONSE_CACHE = new Map();

const TEAM_STAT_DEFINITIONS = [
  { key: 'scorePoints', label: 'Team points', format: 'count', aliases: ['scorepoints', 'teampoints'] },
  { key: 'kills', label: 'Kills', format: 'count', aliases: ['kills', 'kill', 'k'] },
  { key: 'attackErrors', label: 'Attack errors', format: 'count', aliases: ['attackerrors', 'attackerror', 'errors', 'error', 'e'] },
  { key: 'attackAttempts', label: 'Attack attempts', format: 'count', aliases: ['attackattempts', 'attackattempt', 'totalattempts', 'totalattacks', 'attempts', 'attacks', 'ta'] },
  { key: 'hittingPercentage', label: 'Hitting %', format: 'percentage', aliases: ['hittingpercentage', 'attackpercentage', 'hittingpct', 'attackpct', 'pct', 'percentage'] },
  { key: 'assists', label: 'Assists', format: 'count', aliases: ['assists', 'a'] },
  { key: 'serviceAces', label: 'Aces', format: 'count', aliases: ['serviceaces', 'serviceace', 'aces', 'ace', 'sa'] },
  { key: 'serviceErrors', label: 'Service errors', format: 'count', aliases: ['serviceerrors', 'serviceerror', 'serveerrors', 'serveerror', 'se'] },
  { key: 'digs', label: 'Digs', format: 'count', aliases: ['digs', 'dig'] },
  { key: 'soloBlocks', label: 'Solo blocks', format: 'count', aliases: ['soloblocks', 'soloblock', 'blocksolos', 'blocksolo', 'bs'] },
  { key: 'blockAssists', label: 'Block assists', format: 'count', aliases: ['blockassists', 'blockassist', 'ba'] },
  { key: 'totalBlocks', label: 'Total blocks', format: 'decimal', aliases: ['totalblocks', 'totalblock', 'blocks', 'blockstotal', 'blk'] },
  { key: 'blockErrors', label: 'Block errors', format: 'count', aliases: ['blockerrors', 'blockerror', 'blockingerrors', 'be'] },
  { key: 'receptionErrors', label: 'Reception errors', format: 'count', aliases: ['receptionerrors', 'receptionerror', 'receiveerrors', 'receiveerror', 're'] },
  { key: 'ballHandlingErrors', label: 'Ball-handling errors', format: 'count', aliases: ['ballhandlingerrors', 'ballhandlingerror', 'bhe'] },
];

const PLAYER_COLUMNS = [
  {
    key: 'setsPlayed',
    label: 'SP',
    aliases: [
      'setsplayed', 'setplayed', 'setsparticipated', 'setappearances',
      'gamesplayed', 'sp', 'gp', 'sets', 's',
    ],
  },
  { key: 'kills', label: 'K', aliases: ['kills', 'kill', 'k'] },
  { key: 'attackErrors', label: 'E', aliases: ['attackerrors', 'attackerror', 'errors', 'error', 'e'] },
  { key: 'attackAttempts', label: 'TA', aliases: ['attackattempts', 'attackattempt', 'totalattempts', 'totalattacks', 'attempts', 'attacks', 'ta'] },
  { key: 'hittingPercentage', label: 'PCT', aliases: ['hittingpercentage', 'attackpercentage', 'hittingpct', 'attackpct', 'pct'] },
  { key: 'assists', label: 'A', aliases: ['assists', 'a'] },
  { key: 'serviceAces', label: 'SA', aliases: ['serviceaces', 'serviceace', 'aces', 'ace', 'sa'] },
  { key: 'serviceErrors', label: 'SE', aliases: ['serviceerrors', 'serviceerror', 'serveerrors', 'serveerror', 'se'] },
  { key: 'digs', label: 'DIG', aliases: ['digs', 'dig'] },
  { key: 'soloBlocks', label: 'BS', aliases: ['soloblocks', 'soloblock', 'blocksolos', 'blocksolo', 'bs'] },
  { key: 'blockAssists', label: 'BA', aliases: ['blockassists', 'blockassist', 'ba'] },
  { key: 'blockErrors', label: 'BE', aliases: ['blockerrors', 'blockerror', 'blockingerrors', 'be'] },
  { key: 'receptionErrors', label: 'RE', aliases: ['receptionerrors', 'receptionerror', 'receiveerrors', 'receiveerror', 're'] },
  { key: 'ballHandlingErrors', label: 'BHE', aliases: ['ballhandlingerrors', 'ballhandlingerror', 'bhe'] },
  { key: 'points', label: 'PTS', aliases: ['points', 'totalpoints', 'pointstotal', 'pts'] },
];

export async function getGameFlow({
  gameId,
  live = false,
  signal,
  game = null,
  scope = 'flow',
  force = false,
  timeoutMs = 20000,
}) {
  const normalizedGameId = String(gameId || '').trim();
  const normalizedScope = ['flow', 'stats', 'all'].includes(scope) ? scope : 'flow';

  if (!/^[1-9]\d*$/.test(normalizedGameId)) {
    throw new Error('A valid NCAA game ID is required.');
  }

  const cacheKey = `${normalizedGameId}:${normalizedScope}`;
  const cached = RESPONSE_CACHE.get(cacheKey);
  const cacheTtl = live ? 5_000 : 5 * 60_000;
  if (!force && cached && !(cached.live && !live) && Date.now() - cached.storedAt < cacheTtl) {
    return normalizeGameFlow(cached.payload, { game });
  }

  const params = new URLSearchParams({
    gameId: normalizedGameId,
    live: live ? '1' : '0',
    scope: normalizedScope,
  });
  if (force || live) params.set('refresh', String(Date.now()));

  const { response, payload } = await fetchApiJson(`/api/game-flow?${params.toString()}`, {
    timeoutMs,
    headers: { Accept: 'application/json' },
    signal,
    cache: force || live ? 'no-store' : 'default',
  });

  if (!response.ok) {
    throw new Error(payload?.message || DEFAULT_ERROR);
  }

  const normalized = normalizeGameFlow(payload, { game });
  RESPONSE_CACHE.delete(cacheKey);
  RESPONSE_CACHE.set(cacheKey, { payload, live, storedAt: Date.now() });
  while (RESPONSE_CACHE.size > 24) RESPONSE_CACHE.delete(RESPONSE_CACHE.keys().next().value);
  return normalized;
}

export function normalizeGameFlow(payload, context = {}) {
  const payloads = payload?.payloads || {};
  const rawGame = unwrapObject(payloads.game);
  const teamContext = createTeamContext(rawGame, context.game);
  const teamNames = {
    away: teamContext.away.name,
    home: teamContext.home.name,
  };

  const playByPlay = normalizeRallyHistory(normalizeEvents(payloads.playByPlay, {
    source: 'play-by-play',
    teamNames,
    teamContext,
  }));
  const scoringSummary = normalizeRallyHistory(normalizeEvents(payloads.scoringSummary, {
    source: 'scoring-summary',
    teamNames,
    teamContext,
  }));
  const playByPlayEvents = playByPlay.events;
  const scoringSummaryEvents = scoringSummary.events;
  let events = playByPlayEvents.length ? playByPlayEvents : scoringSummaryEvents;
  const feedIntegrity = playByPlayEvents.length ? playByPlay : scoringSummary;
  const eventSetScores = deriveSetScoresFromEvents(events);
  let match = normalizeMatchSnapshotFromPayload(rawGame, teamContext, { ...context.game, gameId: payload?.gameId || context.game?.gameId }, eventSetScores);
  // Observation time belongs to this response, not the client scorecard it enriches.
  match = { ...match, fetchedAt: payload?.fetchedAt || '', sourceUpdatedAt: undefined };
  const official = payload?.officialMatch;
  if (official) {
    const reversed = canonicalTeamSlug(match.away?.slug) === canonicalTeamSlug(official.home?.slug);
    const away = reversed ? official.home : official.away;
    const home = reversed ? official.away : official.home;
    const completed = match.away.setScores.map((score, i) => isCompletedSet(score, match.home.setScores[i], i + 1));
    const changesStatus = official.final || ['suspended', 'postponed', 'canceled'].includes(official.status);
    match = mergeMatchSnapshots(match, {
      ...match, ...(changesStatus ? official : {}), gameId: payload.gameId,
      date: official.date || match.date,
      ...(!official.startTimeIso ? { startTimeIso: match.startTimeIso,
        startTimeEpoch: match.startTimeEpoch, startTime: match.startTime,
        timeLabel: match.timeLabel, timeConfidence: match.timeConfidence } : {}),
      venue: official.venue || match.venue, network: official.network || match.network,
      neutralSite: official.neutralSite ?? match.neutralSite,
      ...(official.startTimeIso ? { startTimeIso: official.startTimeIso, startTimeEpoch: official.startTimeEpoch, timeLabel: official.timeLabel, metadataVerifiedAt: official.verifiedAt } : {}),
      authoritative: changesStatus || match.authoritative, sourceUpdatedAt: official.verifiedAt,
      away: { ...match.away, score: official.final ? away.score : match.away.score, setScores: match.away.setScores.map((score, i) => !official.final || completed[i] ? score : null) },
      home: { ...match.home, score: official.final ? home.score : match.home.score, setScores: match.home.setScores.map((score, i) => !official.final || completed[i] ? score : null) },
    });
  }
  const contradictorySets = new Set(events.filter((event) => {
    const i = event.setNumber - 1;
    const a = match.away.setScores[i];
    const h = match.home.setScores[i];
    return isCompletedSet(a, h, event.setNumber) && (event.awayScore > a || event.homeScore > h);
  }).map((event) => event.setNumber));
  if (contradictorySets.size) {
    const rejected = events.filter((event) => contradictorySets.has(event.setNumber)).length;
    events = events.filter((event) => !contradictorySets.has(event.setNumber));
    feedIntegrity.rejected += rejected;
    feedIntegrity.incomplete = true;
    feedIntegrity.contradictorySets = [...contradictorySets];
  }
  const statPayloads = [payloads.teamStats, payloads.boxscore].filter(Boolean);
  const entries = extractTeamEntries(statPayloads, teamContext);
  const playerTables = mergePlayerTables(
    normalizePlayerTables(entries, teamContext),
    normalizeLegacyPlayerTables(statPayloads, teamContext)
  );
  const teamStats = normalizeTeamStats(statPayloads, entries, teamContext, match, playerTables);

  return {
    gameId: String(payload?.gameId || ''),
    source: String(payload?.source || 'NCAA'),
    fetchedAt: String(payload?.fetchedAt || ''),
    game: rawGame,
    match,
    availability: {
      game: Boolean(payload?.availability?.game),
      playByPlay: playByPlayEvents.length > 0,
      scoringSummary: scoringSummaryEvents.length > 0,
      teamStats: teamStats.length > 0,
      boxscore: playerTables.length > 0,
    },
    routeSources: payload?.routeSources || {},
    flowSource: playByPlayEvents.length
      ? 'play-by-play'
      : scoringSummaryEvents.length
        ? 'scoring-summary'
        : '',
    events,
    feedIntegrity,
    teamStats,
    playerTables,
    raw: payloads,
  };
}

function unwrapObject(value) {
  if (!value || typeof value !== 'object') return {};
  return value.game || value.data?.game || value.data || value;
}

function createTeamContext(gamePayload, clientGame) {
  const detailed = extractDetailedTeams(gamePayload);
  const clientAway = buildTeamIdentity(clientGame?.away, 'away');
  const clientHome = buildTeamIdentity(clientGame?.home, 'home');

  const awayDetail = detailed.find((team) => team.side === 'away') ||
    detailed.find((team) => identitiesMatch(team, clientAway)) || null;
  const homeDetail = detailed.find((team) => team.side === 'home') ||
    detailed.find((team) => identitiesMatch(team, clientHome)) || null;

  return {
    away: mergeTeamIdentity(clientAway, awayDetail, 'away'),
    home: mergeTeamIdentity(clientHome, homeDetail, 'home'),
  };
}

function buildTeamIdentity(value, forcedSide = '') {
  const team = value && typeof value === 'object' ? value : {};
  return {
    side: forcedSide || inferExplicitSide(team),
    name: firstString(
      team.displayName,
      team.fullName,
      team.nameFull,
      team.nameShort,
      team.shortName,
      team.schoolName,
      team.name,
      team.teamName,
      typeof value === 'string' ? value : ''
    ),
    id: cleanText(firstValue(
      team.teamId,
      team.teamID,
      team.id,
      team.schoolId,
      team.schoolID,
      team.ncaaId,
      team.ncaaID
    )),
    slug: firstString(
      team.slug,
      team.seoname,
      team.seoName,
      team.teamSeo,
      team.team_seo,
      team.seo,
      team.urlName,
      team.url_name
    ),
    raw: team,
  };
}

function mergeTeamIdentity(primary, secondary, side) {
  return {
    side,
    name: firstString(primary?.name, secondary?.name, side === 'away' ? 'Away' : 'Home'),
    id: firstString(primary?.id, secondary?.id),
    slug: firstString(primary?.slug, secondary?.slug),
    raw: secondary?.raw || primary?.raw || {},
  };
}

function extractDetailedTeams(gamePayload) {
  const output = [];
  const seen = new Set();
  const contest = findContest(gamePayload);

  function add(value, side = '') {
    if (!value || typeof value !== 'object') return;
    const identity = buildTeamIdentity(value, side || inferExplicitSide(value));
    if (!identity.name && !identity.id) return;
    const key = identity.id || normalizeName(identity.name);
    if (seen.has(key)) return;
    seen.add(key);
    output.push(identity);
  }

  [gamePayload, contest].forEach((container) => {
    if (!container || typeof container !== 'object') return;
    add(container.away, 'away');
    add(container.awayTeam, 'away');
    add(container.visitor, 'away');
    add(container.visitingTeam, 'away');
    add(container.home, 'home');
    add(container.homeTeam, 'home');
    add(container.host, 'home');
    add(container.teams?.away, 'away');
    add(container.teams?.home, 'home');
    firstArray(container.teams, container.competitors, container.contestants, container.participants)
      .forEach((team) => add(team));
  });

  return output;
}

function findContest(value) {
  if (!value || typeof value !== 'object') return {};
  if (Array.isArray(value)) return value.find((item) => item && typeof item === 'object') || {};
  if (Array.isArray(value.contests) && value.contests.length) return value.contests[0];
  if (value.contest && typeof value.contest === 'object') return value.contest;
  if (value.game && typeof value.game === 'object') return findContest(value.game);
  if (value.data && typeof value.data === 'object') return findContest(value.data);
  return value;
}

function inferExplicitSide(value) {
  if (!value || typeof value !== 'object') return '';
  const marker = normalizeName(firstString(
    value.homeAway,
    value.designation,
    value.side,
    value.locationType,
    value.teamLocation,
    value.type
  ));
  if (['away', 'visitor', 'visiting'].includes(marker)) return 'away';
  if (['home', 'host'].includes(marker)) return 'home';
  if (value.isHome === true || value.home === true || value.isHost === true) return 'home';
  if (value.isHome === false || value.home === false) return 'away';
  if (value.isAway === true || value.away === true || value.isVisitor === true) return 'away';
  return '';
}

function identitiesMatch(left, right) {
  if (!left || !right) return false;
  if (left.id && right.id && left.id === right.id) return true;
  const a = normalizeName(left.name);
  const b = normalizeName(right.name);
  return Boolean(a && b && (a === b || a.includes(b) || b.includes(a)));
}

function normalizeMatchSnapshotFromPayload(gamePayload, teamContext, clientGame, eventScores) {
  const contest = findContest(gamePayload);
  const awayTeam = findTeamObject(contest, teamContext.away, 'away');
  const homeTeam = findTeamObject(contest, teamContext.home, 'home');
  const sources = [
    { away: extractSetScores(contest, teamContext.away, 'away'), home: extractSetScores(contest, teamContext.home, 'home') },
    { away: parseSetScoreArray(clientGame?.away?.setScores, 'away'), home: parseSetScoreArray(clientGame?.home?.setScores, 'home') },
    eventScores,
  ];
  const pairedScores = { away: [], home: [] };
  const count = Math.max(0, ...sources.flatMap((source) => [source.away?.length || 0, source.home?.length || 0]));
  for (let i = 0; i < count; i += 1) {
    const source = sources.find((entry) => Number.isFinite(entry.away?.[i]) && Number.isFinite(entry.home?.[i]));
    pairedScores.away[i] = source?.away[i] ?? null;
    pairedScores.home[i] = source?.home[i] ?? null;
  }

  return normalizeCanonicalMatch({
    ...clientGame,
    authoritative: Boolean(awayTeam && homeTeam),
    scoreSource: awayTeam && homeTeam ? 'ncaa-game-detail' : 'rally-fallback',
    rawStatus: firstString(
      contest?.gameState,
      contest?.status,
      contest?.statusCodeDisplay,
      contest?.currentPeriod,
      gamePayload?.gameState,
      gamePayload?.status,
      gamePayload?.currentPeriod,
      clientGame?.status
    ),
    status: firstString(contest?.status, gamePayload?.status, clientGame?.status),
    statusLabel: firstString(contest?.statusLabel, gamePayload?.statusLabel, clientGame?.statusLabel),
    currentPeriod: firstString(contest?.currentPeriod, gamePayload?.currentPeriod, clientGame?.currentPeriod),
    away: buildMatchTeam({
      side: 'away',
      identity: teamContext.away,
      team: awayTeam,
      contest,
      client: clientGame?.away,
      eventScores: eventScores.away,
      pairedScores: pairedScores.away,
    }),
    home: buildMatchTeam({
      side: 'home',
      identity: teamContext.home,
      team: homeTeam,
      contest,
      client: clientGame?.home,
      eventScores: eventScores.home,
      pairedScores: pairedScores.home,
    }),
  });
}

function buildMatchTeam({ side, identity, team, contest, client, pairedScores }) {
  const setScores = pairedScores;
  const explicitScore = finiteNumber(firstValue(
    team?.score,
    team?.setsWon,
    team?.matchScore,
    contest?.[`${side}Score`],
    contest?.scores?.[side],
    contest?.teams?.[side]?.score,
    client?.score
  ));

  return {
    id: identity.id,
    name: firstString(client?.name, identity.name, side === 'away' ? 'Away' : 'Home'),
    shortName: firstString(client?.shortName, client?.nameShort, identity.name),
    slug: firstString(client?.slug, identity.slug),
    logo: firstString(client?.logo, client?.logoUrl),
    score: explicitScore,
    setScores,
    record: firstString(extractTeamRecord(team, contest, side), client?.record),
    conference: firstString(client?.conference, extractTeamConference(team)),
    rank: finiteNumber(firstValue(client?.rank, extractTeamRank(team))),
  };
}

function findTeamObject(contest, identity, side) {
  const explicit = firstObject(
    contest?.[side],
    contest?.[`${side}Team`],
    side === 'away' ? contest?.visitor : null,
    side === 'home' ? contest?.host : null,
    contest?.teams?.[side]
  );
  if (explicit) return explicit;

  return firstArray(contest?.teams, contest?.competitors, contest?.contestants, contest?.participants)
    .find((team) => {
      const candidate = buildTeamIdentity(team);
      return inferExplicitSide(team) === side || identitiesMatch(candidate, identity);
    }) || null;
}

function extractSetScores(contest, identity, side) {
  const team = findTeamObject(contest, identity, side);
  const candidates = [
    team?.setScores,
    team?.sets,
    team?.lineScores,
    team?.linescores,
    team?.periodScores,
    team?.periods,
    contest?.teams?.[side]?.setScores,
    contest?.teams?.[side]?.linescores,
    contest?.scores?.[side],
  ];

  for (const candidate of candidates) {
    const parsed = parseSetScoreArray(candidate, side);
    if (parsed.some((score) => score !== null)) return parsed;
  }

  const container = firstValue(
    contest?.linescores,
    contest?.lineScores,
    contest?.setScores,
    contest?.periodScores,
    contest?.scoresByPeriod
  );
  return parseLineScoreContainer(container, identity, side);
}

function parseLineScoreContainer(value, identity, side) {
  if (!value) return [];
  if (typeof value === 'object' && !Array.isArray(value)) {
    const branch = value[side] || value[side === 'away' ? 'visitor' : 'host'];
    const parsed = parseSetScoreArray(branch, side);
    if (parsed.length) return parsed;
  }
  if (!Array.isArray(value)) return [];

  const teamRow = value.find((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    return identitiesMatch(buildTeamIdentity(row), identity) || inferExplicitSide(row) === side;
  });
  if (teamRow) {
    const parsed = parseSetScoreArray(
      firstValue(teamRow.setScores, teamRow.linescores, teamRow.lineScores, teamRow.periods, teamRow.scores),
      side
    );
    if (parsed.length) return parsed;
  }

  const periods = value
    .map((row, index) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
      const score = finiteNumber(firstValue(
        row[`${side}Score`],
        row[side],
        side === 'away' ? firstValue(row.visit, row.visitor, row.visitorScore) : row.homeScore,
        row.teams?.[side]?.score
      ));
      const period = positiveInteger(firstValue(row.period, row.set, row.setNumber, row.number, index + 1));
      return score !== null && period ? { period, score } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.period - b.period);

  const result = Array.from({ length: Math.min(5, Math.max(0, ...periods.map((row) => row.period))) }, () => null);
  periods.forEach(({ period, score }) => { if (period <= 5) result[period - 1] = score; });
  return result;
}

function parseSetScoreArray(value, side = '') {
  if (!Array.isArray(value)) return [];
  const scores = value.slice(0, 5).map((item) => {
    if (typeof item === 'number' || typeof item === 'string') return finiteNumber(item);
    if (!item || typeof item !== 'object') return null;
    return finiteNumber(firstValue(
      item.score,
      item.points,
      item.value,
      item.displayValue,
      item.teamScore,
      item[`${side}Score`],
      item[side]
    ));
  });
  return scores.some((score) => score !== null) ? scores : [];
}

function mergeSetScores(...candidates) {
  const arrays = candidates
    .map((value) => parseSetScoreArray(value))
    .filter((value) => value.length);
  const length = Math.max(0, ...arrays.map((value) => value.length));
  const result = Array.from({ length }, () => null);

  arrays.forEach((scores) => {
    scores.forEach((score, index) => {
      if (result[index] === null && score !== null) result[index] = score;
    });
  });

  while (result.length && result.at(-1) === null) result.pop();
  return result;
}

function deriveSetScoresFromEvents(events) {
  const bySet = new Map();
  events.forEach((event) => {
    if (!positiveInteger(event.setNumber) || !Number.isInteger(event.awayScore) || !Number.isInteger(event.homeScore)) return;
    const current = bySet.get(event.setNumber);
    const total = event.awayScore + event.homeScore;
    const currentTotal = current ? current.awayScore + current.homeScore : -1;
    if (!current || total > currentTotal || (total === currentTotal && event.sequence >= current.sequence)) {
      bySet.set(event.setNumber, event);
    }
  });

  const maxSet = Math.max(0, ...bySet.keys());
  return {
    away: Array.from({ length: maxSet }, (_, index) => bySet.get(index + 1)?.awayScore ?? null),
    home: Array.from({ length: maxSet }, (_, index) => bySet.get(index + 1)?.homeScore ?? null),
  };
}

function extractTeamRecord(team, contest, side) {
  const candidates = [
    team?.record,
    team?.overallRecord,
    team?.seasonRecord,
    team?.recordSummary,
    team?.records?.overall,
    team?.records?.season,
    team?.records,
    contest?.[`${side}Record`],
    contest?.teams?.[side]?.record,
  ];
  for (const candidate of candidates) {
    const formatted = formatRecord(candidate);
    if (formatted) return formatted;
  }
  return '';
}

function formatRecord(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    const text = cleanText(value);
    return /\d+\s*[-–]\s*\d+/.test(text) ? text.replace(/\s+/g, '') : '';
  }
  if (Array.isArray(value)) {
    const overall = value.find((item) => /overall|season/i.test(firstString(item?.type, item?.name, item?.label))) || value[0];
    return formatRecord(overall);
  }
  if (typeof value !== 'object') return '';
  const summary = firstString(value.summary, value.displayValue, value.value, value.record, value.overall);
  if (/\d+\s*[-–]\s*\d+/.test(summary)) return summary.replace(/\s+/g, '');
  const wins = finiteNumber(firstValue(value.wins, value.win, value.overallWins));
  const losses = finiteNumber(firstValue(value.losses, value.loss, value.overallLosses));
  return wins !== null && losses !== null ? `${wins}-${losses}` : '';
}

function extractTeamConference(team) {
  return firstString(
    team?.conference?.shortName,
    team?.conference?.abbreviation,
    team?.conference?.name,
    team?.conferenceName,
    team?.conferenceShortName,
    team?.conferenceAbbreviation
  );
}

function extractTeamRank(team) {
  return positiveInteger(firstValue(team?.rank, team?.ranking, team?.nationalRank, team?.seed)) || null;
}

function normalizeEvents(payload, { source, teamNames, teamContext }) {
  if (!payload) return [];
  const rows = [];
  collectRows(payload, rows, { setNumber: null, depth: 0 });
  const normalized = [];
  const seen = new Set();

  rows.forEach((row, index) => {
    if (normalized.length >= MAX_EVENTS) return;
    const event = normalizeEvent(row.value, {
      source,
      index,
      inheritedSet: row.setNumber,
      teamNames,
    });
    if (!event) return;
    const teamId = String(row.teamId || '');
    if (/^(kill by|service ace)/i.test(event.description) && teamId) {
      event.scoringSide = teamId === teamContext?.away?.id ? 'away'
        : teamId === teamContext?.home?.id ? 'home' : '';
    }
    const key = `${event.setNumber}|${event.awayScore}|${event.homeScore}|${event.description}|${event.teamSide}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(event);
  });

  normalized.sort((a, b) =>
    (a.setNumber || 0) - (b.setNumber || 0) ||
    (a.sequence || 0) - (b.sequence || 0)
  );
  // Establish one orientation for the whole feed from point-winning actions
  // with explicit team IDs, never by swapping individual regressing rows.
  let direct = 0;
  let reversed = 0;
  const previous = new Map();
  normalized.forEach((event) => {
    if (!Number.isFinite(event.awayScore) || !Number.isFinite(event.homeScore)) return;
    const last = previous.get(event.setNumber) || { awayScore: 0, homeScore: 0 };
    const a = event.awayScore - last.awayScore;
    const h = event.homeScore - last.homeScore;
    if (event.scoringSide && a + h === 1 && a >= 0 && h >= 0) {
      if ((a === 1 ? 'away' : 'home') === event.scoringSide) direct += 1;
      else reversed += 1;
    }
    previous.set(event.setNumber, event);
  });
  if (reversed >= 3 && direct === 0) {
    normalized.forEach((event) => {
      [event.awayScore, event.homeScore] = [event.homeScore, event.awayScore];
      event.teamSide = '';
      event.orientationCorrected = true;
      if (event.awayScore !== null && event.homeScore !== null) event.scoreText = `${event.awayScore}–${event.homeScore}`;
    });
  }
  inferScoringSides(normalized);
  return normalized;
}

export function normalizeRallyHistory(events = []) {
  const accepted = [];
  const previousBySet = new Map();
  const seen = new Set();
  let rejected = 0;
  let gaps = 0;
  let orientationCorrections = 0;

  events.forEach((sourceEvent) => {
    const event = { ...sourceEvent };
    const key = `${event.setNumber}|${event.sequence}|${event.awayScore}|${event.homeScore}|${event.description}`;
    if (seen.has(key)) return;
    seen.add(key);

    if (!Number.isFinite(event.awayScore) || !Number.isFinite(event.homeScore)) {
      accepted.push(event);
      return;
    }

    const setNumber = Number(event.setNumber) || 1;
    const previous = previousBySet.get(setNumber);
    let awayDelta = previous ? event.awayScore - previous.awayScore : event.awayScore;
    let homeDelta = previous ? event.homeScore - previous.homeScore : event.homeScore;

    // Regression is not evidence of reversed team ownership. Reject the row.

    if (previous && (awayDelta < 0 || homeDelta < 0)) {
      rejected += 1;
      return;
    }

    const totalDelta = awayDelta + homeDelta;
    if (previous && totalDelta === 0) return;
    if (totalDelta > 1) {
      event.feedGapBefore = true;
      gaps += 1;
    }

    previousBySet.set(setNumber, event);
    accepted.push(event);
  });

  inferScoringSides(accepted);
  return {
    events: accepted,
    incomplete: rejected > 0 || gaps > 0,
    rejected,
    gaps,
    orientationCorrections,
  };
}

function collectRows(value, output, context) {
  if (value === null || value === undefined || context.depth > MAX_DEPTH || output.length > MAX_EVENTS * 10) return;
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        output.push({ value: item, setNumber: context.setNumber, teamId: item.teamId || context.teamId });
      }
      collectRows(item, output, { ...context, depth: context.depth + 1 });
    });
    return;
  }
  if (typeof value !== 'object') return;

  const ownSet = positiveInteger(firstValue(
    value.setNumber,
    value.set,
    value.setNo,
    value.set_num,
    value.period,
    value.periodNumber,
    value.gameSet
  ));
  const setNumber = ownSet || context.setNumber;

  rowsFromTable(value).forEach((row) => output.push({ value: row, setNumber }));
  Object.values(value).forEach((child) => collectRows(child, output, {
    setNumber,
    teamId: value.teamId || context.teamId,
    depth: context.depth + 1,
  }));
}

function normalizeEvent(row, { source, index, inheritedSet, teamNames }) {
  if (!row || typeof row !== 'object') return null;
  const values = Array.isArray(row) ? row : null;
  const description = cleanText(firstValue(
    values?.find((value) => typeof value === 'string' && /[a-z]/i.test(value) && !/^\d+\s*[-:]\s*\d+$/.test(value)),
    row.description,
    row.text,
    row.playText,
    row.eventText,
    row.summary,
    row.result,
    row.label,
    row.play,
    row.action
  )).replace(/([^\s(])\(/g, '$1 (');
  const setNumber = positiveInteger(firstValue(
    row.setNumber,
    row.set,
    row.setNo,
    row.period,
    row.periodNumber,
    inheritedSet
  ));
  const scoreText = cleanText(firstValue(
    row.scoreText,
    row.score,
    row.displayScore,
    row.currentScore,
    values?.find((value) => /^\s*\d+\s*[-:]\s*\d+\s*$/.test(String(value)))
  ));
  const parsedScore = parseScoreText(scoreText);
  const awayScore = integerValue(firstValue(
    row.awayScore,
    row.visitorScore,
    row.visitingScore,
    row.scoreAway,
    row.away,
    parsedScore?.away
  ));
  const homeScore = integerValue(firstValue(
    row.homeScore,
    row.hostScore,
    row.scoreHome,
    row.home,
    parsedScore?.home
  ));
  const teamValue = firstString(
    row.teamName,
    row.team,
    row.scoringTeam,
    row.school,
    row.participant,
    row.team?.name
  );
  const teamSide = normalizeTeamSide(firstValue(row.homeAway, row.side, row.designation, teamValue), teamNames);
  const clock = normalizeEventClock(firstValue(row.clock, row.time, row.gameClock, row.timestamp));
  const sequence = integerValue(firstValue(row.sequence, row.sequenceNumber, row.order, row.playNumber, row.id, index)) ?? index;
  const hasScore = awayScore !== null && homeScore !== null;

  if (!description && !scoreText && !hasScore) return null;
  if (!hasScore && description && !looksLikeScoringEvent(description)) return null;

  return {
    id: `${source}-${setNumber || 0}-${index}`,
    source,
    sequence,
    setNumber: setNumber || 1,
    description: description || 'Score update',
    clock,
    teamSide,
    teamName: teamValue,
    awayScore,
    homeScore,
    scoreText: hasScore ? `${awayScore}–${homeScore}` : scoreText,
  };
}

function normalizeEventClock(value) {
  const text = cleanText(value);
  const hundredths = text.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (hundredths) {
    return `${hundredths[1].padStart(2, '0')}:${hundredths[2]}.${hundredths[3]}`;
  }
  return text;
}

function inferScoringSides(events) {
  const previousBySet = new Map();
  events.forEach((event) => {
    const previous = previousBySet.get(event.setNumber);
    if (!event.teamSide && previous && event.awayScore !== null && event.homeScore !== null) {
      const awayDelta = event.awayScore - previous.awayScore;
      const homeDelta = event.homeScore - previous.homeScore;
      if (awayDelta > 0 && homeDelta === 0) event.teamSide = 'away';
      if (homeDelta > 0 && awayDelta === 0) event.teamSide = 'home';
    }
    if (event.awayScore !== null && event.homeScore !== null) previousBySet.set(event.setNumber, event);
  });
}

function normalizeTeamSide(value, teamNames) {
  const normalized = normalizeName(typeof value === 'object' ? firstString(value?.name, value?.displayName, value?.homeAway) : value);
  if (!normalized) return '';
  if (['away', 'visitor', 'visiting'].includes(normalized)) return 'away';
  if (['home', 'host'].includes(normalized)) return 'home';
  const away = normalizeName(teamNames.away);
  const home = normalizeName(teamNames.home);
  if (away && (normalized === away || normalized.includes(away) || away.includes(normalized))) return 'away';
  if (home && (normalized === home || normalized.includes(home) || home.includes(normalized))) return 'home';
  return '';
}

function extractTeamEntries(payloads, teamContext) {
  const candidates = [];
  const seen = new WeakSet();
  const teamDirectory = createPayloadTeamDirectory(payloads, teamContext);
  const teamNames = {
    away: teamContext.away.name,
    home: teamContext.home.name,
  };

  function walk(value, inheritedSide = '', depth = 0) {
    if (!value || typeof value !== 'object' || depth > MAX_DEPTH) return;
    if (Array.isArray(value)) {
      value.forEach((item) => walk(item, inheritedSide, depth + 1));
      return;
    }
    if (seen.has(value)) return;
    seen.add(value);

    const identity = buildTeamIdentity(firstObject(value.team, value.school) || value);
    const teamId = cleanText(firstValue(
      value.teamId,
      value.teamID,
      value.schoolId,
      value.schoolID,
      identity.id
    ));
    const directoryTeam = teamId ? teamDirectory.get(teamId) : null;
    const side = inferExplicitSide(value) ||
      directoryTeam?.side ||
      normalizeTeamSide(firstString(identity.name, directoryTeam?.name), teamNames) ||
      inheritedSide;
    const explicitTeamStats = firstObject(
      value.teamStats,
      value.teamStatistics,
      value.statistics?.team,
      value.stats?.team
    );
    const teamStats = explicitTeamStats || (
      !looksLikePlayerRecord(value)
        ? firstObject(value.statistics, value.stats)
        : null
    );
    const playerStats = collectPlayerRecords([
      value.playerStats,
      value.playerStatistics,
      value.players,
      value.athletes,
      value.playerBoxscore,
      value.playerBoxscores,
      value.individualStats,
      value.individualStatistics,
      value.athleteStats,
      value.studentAthletes,
      value.statistics?.players,
      value.stats?.players,
      value.boxscore?.players,
      value.boxScore?.players,
      value.roster?.players,
    ]);

    if (teamStats || playerStats.length) {
      candidates.push({
        side,
        teamName: firstString(
          identity.name,
          directoryTeam?.name,
          side === 'away' ? teamContext.away.name : side === 'home' ? teamContext.home.name : ''
        ),
        teamId,
        teamStats: teamStats || {},
        playerStats,
      });
    }

    Object.entries(value).forEach(([key, child]) => {
      const normalizedKey = normalizeKey(key);
      let nextSide = side;
      if (/away|visitor|visiting/.test(normalizedKey)) nextSide = 'away';
      if (/home|host/.test(normalizedKey)) nextSide = 'home';
      walk(child, nextSide, depth + 1);
    });
  }

  payloads.forEach((payload) => walk(payload));

  const merged = new Map();
  const unresolved = [];

  candidates.forEach((candidate) => {
    let side = candidate.side;
    if (!side && candidate.teamId) {
      side = teamDirectory.get(candidate.teamId)?.side || '';
      if (!side && candidate.teamId === teamContext.away.id) side = 'away';
      if (!side && candidate.teamId === teamContext.home.id) side = 'home';
    }
    if (!side) side = normalizeTeamSide(candidate.teamName, teamNames);
    if (!side) {
      unresolved.push(candidate);
      return;
    }
    mergeTeamEntry(merged, side, candidate, teamContext);
  });

  // Last-resort assignment only applies when NCAA omits both team IDs and home/away markers.
  unresolved.forEach((candidate) => {
    const availableSide = ['away', 'home'].find((side) => !merged.has(side));
    if (availableSide) mergeTeamEntry(merged, availableSide, candidate, teamContext);
  });

  return ['away', 'home'].map((side) => merged.get(side)).filter(Boolean);
}

function createPayloadTeamDirectory(payloads, teamContext) {
  const directory = new Map();
  const seen = new WeakSet();

  function add(value) {
    if (!value || typeof value !== 'object') return;
    const identity = buildTeamIdentity(value);
    if (!identity.id) return;
    const side = inferExplicitSide(value) ||
      (value.isHome === true ? 'home' : value.isHome === false ? 'away' : '') ||
      normalizeTeamSide(identity.name, {
        away: teamContext.away.name,
        home: teamContext.home.name,
      });
    const current = directory.get(identity.id) || {};
    directory.set(identity.id, {
      id: identity.id,
      name: firstString(identity.name, current.name),
      side: firstString(side, current.side),
    });
  }

  function walk(value, depth = 0) {
    if (!value || typeof value !== 'object' || depth > MAX_DEPTH) return;
    if (Array.isArray(value)) {
      value.forEach((item) => walk(item, depth + 1));
      return;
    }
    if (seen.has(value)) return;
    seen.add(value);

    if (
      firstValue(value.teamId, value.teamID, value.schoolId, value.schoolID) !== undefined ||
      value.isHome !== undefined ||
      value.homeAway ||
      value.designation
    ) {
      add(firstObject(value.team, value.school) || value);
      add(value);
    }

    Object.values(value).forEach((child) => walk(child, depth + 1));
  }

  if (teamContext.away.id) {
    directory.set(teamContext.away.id, {
      id: teamContext.away.id,
      name: teamContext.away.name,
      side: 'away',
    });
  }
  if (teamContext.home.id) {
    directory.set(teamContext.home.id, {
      id: teamContext.home.id,
      name: teamContext.home.name,
      side: 'home',
    });
  }
  payloads.forEach((payload) => walk(payload));
  return directory;
}

function collectPlayerRecords(sources) {
  const records = [];
  const seen = new WeakSet();
  const recordKeys = new Set();

  function add(value) {
    const key = playerRecordKey(value);
    if (!key || recordKeys.has(key)) return;
    recordKeys.add(key);
    records.push(value);
  }

  function walk(value, depth = 0) {
    if (!value || typeof value !== 'object' || depth > 6) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (looksLikePlayerRecord(item)) add(item);
        else walk(item, depth + 1);
      });
      return;
    }
    if (seen.has(value)) return;
    seen.add(value);
    if (looksLikePlayerRecord(value)) {
      add(value);
      return;
    }
    Object.values(value).forEach((child) => walk(child, depth + 1));
  }

  sources.filter(Boolean).forEach((source) => walk(source));
  return records.slice(0, MAX_PLAYERS);
}

function looksLikePlayerRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const hasIdentity = Boolean(
    value.player ||
    value.athlete ||
    firstString(value.firstName, value.lastName, value.playerName, value.athleteName, value.fullName, value.displayName)
  );
  if (!hasIdentity) return false;
  const keys = Object.keys(firstObject(value.stats, value.statistics, value.playerStats, value) || value)
    .map(normalizeKey);
  return PLAYER_COLUMNS.some((column) =>
    keys.some((key) =>
      normalizeKey(column.key) === key ||
      column.aliases.some((alias) => normalizeKey(alias) === key)
    )
  );
}

function playerRecordKey(value) {
  if (!value || typeof value !== 'object') return '';
  const id = cleanText(firstValue(
    value.playerId,
    value.playerID,
    value.athleteId,
    value.athleteID,
    value.id
  ));
  if (id) return `id:${id}`;
  const name = firstString(
    value.player?.fullName,
    value.player?.displayName,
    value.player?.name,
    value.athlete?.fullName,
    value.athlete?.displayName,
    value.athlete?.name,
    [value.firstName, value.lastName].filter(Boolean).join(' '),
    value.fullName,
    value.displayName,
    value.name,
    value.playerName,
    value.athleteName
  );
  return name ? `name:${normalizeName(name)}` : '';
}

function mergeTeamEntry(merged, side, candidate, teamContext) {
  const current = merged.get(side) || {
    side,
    teamName: side === 'away' ? teamContext.away.name : teamContext.home.name,
    teamId: side === 'away' ? teamContext.away.id : teamContext.home.id,
    teamStats: {},
    playerStats: [],
  };

  current.teamName = firstString(current.teamName, candidate.teamName);
  current.teamId = firstString(current.teamId, candidate.teamId);
  current.teamStats = mergeLooseObjects(current.teamStats, candidate.teamStats);
  current.playerStats = mergeRawPlayerRecords(current.playerStats, candidate.playerStats);
  merged.set(side, current);
}

function mergeLooseObjects(primary, secondary) {
  const result = { ...(primary || {}) };
  Object.entries(secondary || {}).forEach(([key, value]) => {
    const current = result[key];
    if (current === undefined || current === null || current === '' ||
        (Array.isArray(current) && !current.length) ||
        (typeof current === 'object' && !Array.isArray(current) && !Object.keys(current).length)) {
      result[key] = value;
    }
  });
  return result;
}

function mergeRawPlayerRecords(primary, secondary) {
  const records = new Map();
  [...(primary || []), ...(secondary || [])].forEach((player, index) => {
    const key = playerRecordKey(player) || `index:${index}`;
    const current = records.get(key);
    records.set(key, current ? mergeLooseObjects(current, player) : player);
  });
  return Array.from(records.values()).slice(0, MAX_PLAYERS);
}

function normalizePlayerTables(entries, teamContext) {
  return entries
    .map((entry) => {
      const players = entry.playerStats
        .slice(0, MAX_PLAYERS)
        .map(normalizePlayer)
        .filter(Boolean);
      if (!players.length) return null;
      const visibleColumns = PLAYER_COLUMNS.filter((column) =>
        players.some((player) => player.stats[column.key] !== '')
      );
      return {
        id: `players-${entry.side}`,
        side: entry.side,
        title: entry.side === 'away' ? teamContext.away.name : teamContext.home.name,
        headers: ['Player', ...visibleColumns.map((column) => column.label)],
        positions: players.map((player) => player.position),
        jerseys: players.map((player) => player.jersey),
        rows: players.map((player) => [
          player.name,
          ...visibleColumns.map((column) => player.stats[column.key] || '—'),
        ]),
      };
    })
    .filter(Boolean);
}

function mergePlayerTables(...groups) {
  const bySide = new Map();

  groups.flat().filter(Boolean).forEach((table) => {
    if (!table.side) return;
    const candidates = bySide.get(table.side) || [];
    candidates.push(table);
    bySide.set(table.side, candidates);
  });

  return ['away', 'home'].map((side) => {
    const candidates = bySide.get(side) || [];
    if (!candidates.length) return null;

    const best = candidates.reduce((current, candidate) => {
      const currentCells = current.rows.length * current.headers.length;
      const candidateCells = candidate.rows.length * candidate.headers.length;
      return candidateCells > currentCells ? candidate : current;
    });
    const positionsByPlayer = new Map();
    const jerseysByPlayer = new Map();

    candidates.forEach((table) => {
      table.rows.forEach((row, index) => {
        const key = normalizeName(row?.[0]);
        const position = normalizePlayerPosition(table.positions?.[index]);
        if (key && position && !positionsByPlayer.has(key)) positionsByPlayer.set(key, position);
        const jersey = normalizeJerseyNumber(table.jerseys?.[index]);
        if (key && jersey !== '' && !jerseysByPlayer.has(key)) jerseysByPlayer.set(key, jersey);
      });
    });

    return {
      ...best,
      jerseys: best.rows.map((row) => jerseysByPlayer.get(normalizeName(row?.[0])) || ''),
      positions: best.rows.map((row, index) =>
        positionsByPlayer.get(normalizeName(row?.[0])) || normalizePlayerPosition(best.positions?.[index])
      ),
    };
  }).filter(Boolean);
}

function normalizeLegacyPlayerTables(payloads, teamContext) {
  const candidates = [];
  const seen = new WeakSet();
  const teamNames = {
    away: teamContext.away.name,
    home: teamContext.home.name,
  };

  function walk(node, inheritedSide = '', depth = 0) {
    if (!node || typeof node !== 'object' || depth > MAX_DEPTH || seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, inheritedSide, depth + 1));
      return;
    }

    const identity = buildTeamIdentity(firstObject(node.team, node.school, node) || node);
    const ownSide = inferExplicitSide(node) || normalizeTeamSide(identity.name, teamNames) || inheritedSide;
    const rows = rowsFromTable(node);
    const rawHeaders = firstArray(node.headers, node.columns, node.headings, node.labels)
      .map((header) => cleanText(firstValue(header?.label, header?.name, header?.text, header?.value, header)));

    if (rows.length && rawHeaders.length) {
      const playerIndex = rawHeaders.findIndex((header) => {
        const key = normalizeKey(header);
        return ['player', 'playername', 'athlete', 'athletename', 'name', 'studentathlete'].includes(key);
      });
      const positionIndex = rawHeaders.findIndex((header) =>
        ['position', 'pos', 'playerposition'].includes(normalizeKey(header))
      );
      const jerseyIndex = rawHeaders.findIndex((header) =>
        readPlayerJersey({ [header]: '0' }) === '0'
      );
      const statColumns = rawHeaders
        .map((header, index) => ({ definition: findPlayerColumn(header), index }))
        .filter((item) => item.definition && item.index !== playerIndex);

      if (playerIndex >= 0 && statColumns.length) {
        const normalizedRows = rows
          .map((row) => {
            const playerName = cleanText(row[playerIndex]);
            if (!playerName || /team totals?|totals?/i.test(playerName)) return null;
            const stats = {};
            statColumns.forEach(({ definition, index }) => {
              const value = cleanText(row[index]);
              if (value) stats[definition.key] = definition.key === 'setsPlayed'
                ? formatPlayerStat(value, definition.key) : value;
            });
            return {
              playerName,
              jersey: jerseyIndex >= 0 ? normalizeJerseyNumber(row[jerseyIndex]) : '',
              position: positionIndex >= 0 ? normalizePlayerPosition(row[positionIndex]) : '',
              stats,
            };
          })
          .filter(Boolean);

        if (normalizedRows.length) {
          candidates.push({
            side: ownSide,
            teamName: firstString(identity.name, node.title, node.name),
            rows: normalizedRows,
          });
        }
      }
    }

    Object.entries(node).forEach(([key, child]) => {
      const normalizedKey = normalizeKey(key);
      let nextSide = ownSide;
      if (/away|visitor|visiting/.test(normalizedKey)) nextSide = 'away';
      if (/home|host/.test(normalizedKey)) nextSide = 'home';
      walk(child, nextSide, depth + 1);
    });
  }

  payloads.forEach((payload) => walk(payload));

  const accumulators = {
    away: createPlayerAccumulator('away', teamContext.away.name),
    home: createPlayerAccumulator('home', teamContext.home.name),
  };

  candidates.forEach((candidate, index) => {
    let side = candidate.side;
    if (!side) side = index % 2 === 0 ? 'away' : 'home';
    const accumulator = accumulators[side];
    if (!accumulator) return;

    candidate.rows.forEach((row) => {
      const playerKey = normalizeName(row.playerName);
      if (!playerKey) return;
      const current = accumulator.players.get(playerKey) || {
        name: row.playerName,
        position: '',
        stats: {},
      };
      if (!current.position && row.position) current.position = row.position;
      if (!current.jersey && row.jersey) current.jersey = row.jersey;
      Object.entries(row.stats).forEach(([key, value]) => {
        if (!current.stats[key] && value) current.stats[key] = value;
        accumulator.columns.add(key);
      });
      accumulator.players.set(playerKey, current);
    });
  });

  return ['away', 'home']
    .map((side) => buildLegacyPlayerTable(accumulators[side]))
    .filter(Boolean);
}

function createPlayerAccumulator(side, title) {
  return {
    side,
    title,
    columns: new Set(),
    players: new Map(),
  };
}

function buildLegacyPlayerTable(accumulator) {
  if (!accumulator?.players.size) return null;
  const visibleColumns = PLAYER_COLUMNS.filter((column) => accumulator.columns.has(column.key));
  if (!visibleColumns.length) return null;

  const players = Array.from(accumulator.players.values());
  return {
    id: `players-${accumulator.side}-legacy`,
    side: accumulator.side,
    title: accumulator.title,
    headers: ['Player', ...visibleColumns.map((column) => column.label)],
    positions: players.map((player) => normalizePlayerPosition(player.position)),
    jerseys: players.map((player) => player.jersey || ''),
    rows: players.map((player) => [
      player.name,
      ...visibleColumns.map((column) => player.stats[column.key] || '—'),
    ]),
  };
}

function findPlayerColumn(value) {
  const key = normalizeKey(value);
  return PLAYER_COLUMNS.find((column) =>
    normalizeKey(column.key) === key ||
    normalizeKey(column.label) === key ||
    column.aliases.some((alias) => normalizeKey(alias) === key)
  ) || null;
}

function normalizePlayerPosition(value) {
  const raw = typeof value === 'object' && value
    ? firstString(
      value.position,
      value.pos,
      value.playerPosition,
      value.player_position,
      value.positionAbbreviation,
      value.positionShort,
      value.positionCode,
      value.player?.position,
      value.athlete?.position
    )
    : cleanText(value);
  if (!raw) return '';

  const normalized = normalizeKey(raw);
  const aliases = {
    outsidehitter: 'OH',
    outside: 'OH',
    middleblocker: 'M',
    middlehitter: 'M',
    middle: 'M',
    rightsidehitter: 'RS',
    rightside: 'RS',
    oppositehitter: 'OPP',
    opposite: 'OPP',
    setter: 'S',
    libero: 'L',
    defensivespecialist: 'DS',
    servingspecialist: 'SS',
    utility: 'U',
  };
  return aliases[normalized] || raw.toUpperCase();
}

function normalizePlayer(value) {
  if (!value || typeof value !== 'object') return null;
  const name = firstString(
    value.player?.fullName,
    value.player?.displayName,
    value.player?.name,
    value.athlete?.fullName,
    value.athlete?.displayName,
    value.athlete?.name,
    [value.firstName, value.lastName].filter(Boolean).join(' '),
    [value.first_name, value.last_name].filter(Boolean).join(' '),
    value.fullName,
    value.displayName,
    value.name,
    value.playerName,
    value.athleteName
  );
  if (!name || /team totals?/i.test(name)) return null;
  const source = firstObject(value.stats, value.statistics, value.playerStats, value) || value;
  const stats = {};
  PLAYER_COLUMNS.forEach((column) => {
    if (column.key === 'setsPlayed') {
      // Prefer explicit player participation over generic set metadata, even
      // when the latter occurs earlier in the response or in nested stats.
      // Never use the match/team total or the number of rally appearances.
      let raw;
      for (const alias of column.aliases) {
        for (const candidate of source === value ? [source] : [source, value]) {
          const found = findStatValue(candidate, [alias]);
          if (found !== undefined && found !== null && found !== '') {
            raw = found;
            break;
          }
        }
        if (raw !== undefined) break;
      }
      stats[column.key] = formatPlayerStat(raw, column.key);
      return;
    }
    const nestedValue = findStatValue(source, column.aliases);
    const raw = nestedValue === undefined && source !== value
      ? findStatValue(value, column.aliases)
      : nestedValue;
    stats[column.key] = formatPlayerStat(raw, column.key);
  });
  return { name, jersey: readPlayerJersey(value), position: normalizePlayerPosition(value), stats };
}

function normalizeTeamStats(payloads, entries, teamContext, match, playerTables) {
  const bySide = { away: {}, home: {} };
  const derivedBySide = { away: {}, home: {} };

  entries.forEach((entry) => {
    const official = extractCanonicalStats(entry.teamStats);
    const players = entry.playerStats.map(normalizePlayerRecord).filter(Boolean);
    const derived = aggregatePlayerStats(players);
    bySide[entry.side] = mergeStatSources(bySide[entry.side], official);
    derivedBySide[entry.side] = mergeStatSources(derivedBySide[entry.side], derived);
  });

  const legacyRows = normalizeLegacyTeamStats(payloads, teamContext);
  legacyRows.forEach((row) => {
    const definition = findStatDefinition(row.label);
    if (!definition) return;
    const away = parseStatNumber(row.away);
    const home = parseStatNumber(row.home);
    if (!Number.isFinite(bySide.away[definition.key]) && Number.isFinite(away)) bySide.away[definition.key] = away;
    if (!Number.isFinite(bySide.home[definition.key]) && Number.isFinite(home)) bySide.home[definition.key] = home;
  });

  ['away', 'home'].forEach((side) => {
    bySide[side] = mergeStatSources(bySide[side], derivedBySide[side]);
    const scores = Array.isArray(match?.[side]?.setScores) ? match[side].setScores : [];
    const usable = scores.filter(Number.isFinite);
    if (usable.length && usable.length === scores.length) bySide[side].scorePoints = usable.reduce((sum, value) => sum + value, 0);
  });

  // Missing categories need fallback even when point totals already exist.
  playerTables.forEach((table) => {
    const side = table.side;
    if (!bySide[side] || table.rows.length < 6) return;
    const players = table.rows.filter((row) => !/^(?:team\s*)?totals?$/i.test(cleanText(row[0])));
    const totals = {};
    table.headers.forEach((header, index) => {
      const definition = findStatDefinition(header);
      if (!definition || definition.format === 'percentage' || definition.key === 'scorePoints') return;
      const values = players.map((row) => parseStatNumber(row[index]));
      if (values.length >= 6 && values.every(Number.isFinite)) totals[definition.key] = values.reduce((sum, value) => sum + value, 0);
    });
    bySide[side] = mergeStatSources(bySide[side], totals);
  });

  ['away', 'home'].forEach((side) => completeDerivedStats(bySide[side]));

  return TEAM_STAT_DEFINITIONS
    .map((definition) => {
      const away = bySide.away[definition.key];
      const home = bySide.home[definition.key];
      if (!Number.isFinite(away) && !Number.isFinite(home)) return null;
      return {
        key: definition.key,
        label: definition.label,
        away: formatCanonicalStat(away, definition.format),
        home: formatCanonicalStat(home, definition.format),
      };
    })
    .filter(Boolean);
}

function extractCanonicalStats(source) {
  const result = {};
  TEAM_STAT_DEFINITIONS.forEach((definition) => {
    const value = findStatValue(source, [definition.key, ...definition.aliases]);
    const number = parseStatNumber(value);
    if (Number.isFinite(number)) result[definition.key] = number;
  });
  return result;
}

function normalizePlayerRecord(player) {
  if (!player || typeof player !== 'object') return null;
  const source = firstObject(player.stats, player.statistics, player.playerStats, player) || player;
  return extractCanonicalStats(source);
}

function aggregatePlayerStats(players) {
  const result = {};
  if (players.length < 6) return result;
  TEAM_STAT_DEFINITIONS.forEach(({ key, format }) => {
    if (format === 'percentage' || key === 'scorePoints') return;
    if (players.every((player) => Number.isFinite(player[key]))) {
      result[key] = players.reduce((sum, player) => sum + player[key], 0);
    }
  });
  return result;
}

function completeDerivedStats(result) {
  if (!Number.isFinite(result.hittingPercentage) && ['kills', 'attackErrors', 'attackAttempts'].every((key) => Number.isFinite(result[key])) && result.attackAttempts > 0) {
    result.hittingPercentage = (result.kills - result.attackErrors) / result.attackAttempts;
  }
  // NCAA's generic totalBlocks can count blocking involvements (BS + BA),
  // not team blocks. With both components published, use volleyball scoring.
  if (Number.isFinite(result.soloBlocks) && Number.isFinite(result.blockAssists)) {
    result.totalBlocks = result.soloBlocks + result.blockAssists / 2;
  }
  return result;
}

function mergeStatSources(primary, fallback) {
  const result = {};
  TEAM_STAT_DEFINITIONS.forEach(({ key }) => {
    if (Number.isFinite(primary?.[key])) result[key] = primary[key];
    else if (Number.isFinite(fallback?.[key])) result[key] = fallback[key];
  });
  return result;
}

function normalizeLegacyTeamStats(payloads, teamContext) {
  const rows = [];
  const seen = new Set();
  payloads.forEach((payload) => {
    extractTables(payload).forEach((table) => {
      if (table.rows.length === 2 && table.headers.length > 1) {
        const firstSide = normalizeTeamSide(table.rows[0][0], {
          away: teamContext.away.name,
          home: teamContext.home.name,
        }) || 'away';
        const secondSide = firstSide === 'away' ? 'home' : 'away';
        table.headers.slice(1).forEach((header, index) => {
          const label = cleanText(header);
          if (!label || /team|school|name/i.test(label)) return;
          const row = {
            label,
            [firstSide]: cleanText(table.rows[0][index + 1]),
            [secondSide]: cleanText(table.rows[1][index + 1]),
          };
          const key = normalizeKey(label);
          if (!seen.has(key)) {
            seen.add(key);
            rows.push(row);
          }
        });
      } else if (table.rows.length && table.headers.length >= 3) {
        table.rows.forEach((values) => {
          const label = cleanText(values[0]);
          if (!label || !findStatDefinition(label)) return;
          const key = normalizeKey(label);
          if (seen.has(key)) return;
          seen.add(key);
          rows.push({ label, away: cleanText(values[1]), home: cleanText(values[2]) });
        });
      }
    });
  });
  return rows;
}

function extractTables(value) {
  const tables = [];
  const seen = new WeakSet();
  function walk(node, depth = 0) {
    if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return;
    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, depth + 1));
      return;
    }
    if (seen.has(node)) return;
    seen.add(node);
    const rows = rowsFromTable(node);
    if (rows.length) {
      const headers = firstArray(node.headers, node.columns, node.headings).map(cleanText);
      tables.push({ headers, rows: rows.map((row) => row.map(cleanText)) });
    }
    Object.values(node).forEach((child) => walk(child, depth + 1));
  }
  walk(value);
  return tables;
}

function rowsFromTable(value) {
  if (!value || typeof value !== 'object') return [];
  const rows = firstArray(value.rows, value.dataRows, value.tableRows);
  return rows
    .map((row) => {
      if (Array.isArray(row)) return row;
      if (!row || typeof row !== 'object') return [];
      const cells = firstArray(row.cells, row.values, row.columns);
      if (cells.length) return cells.map((cell) => firstValue(cell?.value, cell?.displayValue, cell?.text, cell));
      return [];
    })
    .filter((row) => row.length);
}

function findStatValue(source, aliases) {
  if (!source || typeof source !== 'object') return undefined;
  const aliasSet = new Set(aliases.map(normalizeKey));
  for (const [key, value] of Object.entries(source)) {
    if (aliasSet.has(normalizeKey(key))) return unwrapStatValue(value);
  }
  for (const value of Object.values(source)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (!item || typeof item !== 'object') continue;
        const label = normalizeKey(firstString(item.name, item.label, item.statName, item.key, item.abbreviation));
        if (aliasSet.has(label)) return unwrapStatValue(firstValue(item.value, item.displayValue, item.statValue, item.total));
      }
    }
  }
  return undefined;
}

function unwrapStatValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return firstValue(value.value, value.displayValue, value.total, value.count, value.statValue);
  }
  return value;
}

function findStatDefinition(value) {
  const key = normalizeKey(value);
  return TEAM_STAT_DEFINITIONS.find((definition) =>
    normalizeKey(definition.key) === key ||
    normalizeKey(definition.label) === key ||
    definition.aliases.some((alias) => normalizeKey(alias) === key)
  );
}

function formatPlayerStat(value, key) {
  if (key === 'setsPlayed') {
    // Arrays/objects, booleans, percentages and fractional counts are not SP.
    const text = typeof value === 'number' || typeof value === 'string'
      ? String(value).trim() : '';
    return /^[0-5]$/.test(text) ? text : '';
  }
  const number = parseStatNumber(value);
  if (!Number.isFinite(number)) return '';
  if (key === 'hittingPercentage') return formatCanonicalStat(number, 'percentage');
  if (key === 'points') return Number.isInteger(number) ? String(number) : number.toFixed(1);
  return String(Math.round(number));
}

function formatCanonicalStat(value, format) {
  if (!Number.isFinite(value)) return '';
  if (format === 'percentage') {
    const normalized = Math.abs(value) > 1 ? value / 100 : value;
    return normalized.toFixed(3).replace(/^(-?)0\./, '$1.');
  }
  if (format === 'decimal') return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  return String(Math.round(value));
}

function parseStatNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = cleanText(value);
  if (!text || text === '—') return null;
  const number = Number(text.replace(/,/g, '').replace(/%$/, ''));
  if (!Number.isFinite(number)) return null;
  return /%$/.test(text) ? number / 100 : number;
}

function parseScoreText(value) {
  const match = String(value || '').match(/(\d+)\s*[-:]\s*(\d+)/);
  return match ? { away: Number(match[1]), home: Number(match[2]) } : null;
}

function looksLikeScoringEvent(value) {
  return /(kill|ace|block|error|point|score|attack|serve|set\s*point|match\s*point)/i.test(String(value || ''));
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function firstArray(...values) {
  return values.find(Array.isArray) || [];
}

function firstObject(...values) {
  return values.find((value) => value && typeof value === 'object' && !Array.isArray(value)) || null;
}

function cleanText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return cleanText(firstValue(value.displayValue, value.value, value.text, value.label, value.name));
  }
  return repairUtf8Text(String(value)).replace(/\s+/g, ' ').trim();
}

function normalizeName(value) {
  return cleanText(value).toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
}

function normalizeKey(value) {
  return cleanText(value).toLowerCase().replace(/%/g, 'percentage').replace(/[^a-z0-9]+/g, '');
}

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const cleaned = String(value).replace(/[^\d.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
}

function integerValue(value) {
  const number = finiteNumber(value);
  return number === null ? null : Math.trunc(number);
}

function positiveInteger(value) {
  const number = integerValue(value);
  return number && number > 0 ? number : null;
}
